import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { LOCAL_VERSION } from '../utils/versionCheck';

declare const __APP_VERSION__: string | undefined;

export interface AppVersionDisplay {
    version: string;
    build: string;
    versionString: string;       // "Versión 1.5.36"
    fullVersionString: string;   // "Versión 1.5.36 (58)" en nativo, "Versión 1.5.36" en web
}

/**
 * Hook para obtener la versión visible de la aplicación:
 * - En app nativa: lee directamente de App.getInfo() (version y build)
 * - En la web: lee la versión inyectada en el build desde package.json vía Vite define
 * - Nunca escrita a mano en el código.
 */
export function useAppVersionDisplay(): AppVersionDisplay {
    const webVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : LOCAL_VERSION.version;

    const [state, setState] = useState<AppVersionDisplay>({
        version: webVersion,
        build: String(LOCAL_VERSION.build),
        versionString: `Versión ${webVersion}`,
        fullVersionString: `Versión ${webVersion}`
    });

    useEffect(() => {
        let isMounted = true;

        if (Capacitor.isNativePlatform()) {
            CapacitorApp.getInfo().then(info => {
                if (isMounted) {
                    setState({
                        version: info.version,
                        build: info.build,
                        versionString: `Versión ${info.version}`,
                        fullVersionString: `Versión ${info.version} (${info.build})`
                    });
                }
            }).catch(e => {
                console.warn('[useAppVersionDisplay] Error leyendo info de Capacitor:', e);
            });
        }

        return () => {
            isMounted = false;
        };
    }, []);

    return state;
}
